import { ChevronDown, X } from 'lucide-react';
import React from 'react';
import { I18NText } from '@api/ChallengeClient';
import { useLocalizedField } from '@components/hooks/use-localised-field';
import LanguageSelect from '@components/language-select';
import { FieldLabel } from '@components/textfield';
import { Button } from '@components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
} from '@components/ui/dropdown-menu';
import { Input } from '@components/ui/input';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger
} from '@components/ui/tooltip';
import { I18n } from '@core/i18n';
import { roleIconPresets } from './role-icon-presets';

interface RoleLabelFieldProps {
    value: I18NText;
    onChange(value: I18NText): void;
}

const isEmpty = (label: I18NText) =>
    Object.values(label).every((translation) => !translation);

export function RoleLabelField({ value, onChange }: RoleLabelFieldProps) {
    const { selectedValue, options, language, setLanguage } = useLocalizedField(
        value,
        I18n.allLanguageOptions,
        I18n.mainLanguageKey
    );
    const inputId = React.useId();

    const otherLanguages = (label: I18NText) =>
        I18n.allLanguageOptions
            .filter((option) => option.value !== language)
            .map((option) => label[option.value])
            .filter(Boolean)
            .join(' · ');

    const suggestions = roleIconPresets.filter((preset) =>
        Object.values(preset.label).some((translation) =>
            translation?.toLowerCase().includes(selectedValue.toLowerCase())
        )
    );

    return (
        <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor={inputId} label="Role label" />
            <div className="flex items-center gap-1">
                <Input
                    id={inputId}
                    value={selectedValue}
                    placeholder="Pick a suggestion or type your own"
                    onChange={(event) =>
                        onChange({ ...value, [language]: event.target.value })
                    }
                />
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label="Suggestions"
                        >
                            <ChevronDown />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                        align="end"
                        className="max-h-72 overflow-y-auto"
                    >
                        {suggestions.length === 0 && (
                            <DropdownMenuItem disabled>
                                No suggestions
                            </DropdownMenuItem>
                        )}
                        {suggestions.map((preset) => (
                            <DropdownMenuItem
                                key={preset.key}
                                onSelect={() => onChange(preset.label)}
                                className="flex flex-col items-start gap-0"
                            >
                                <span className="text-sm">
                                    {preset.label[language] ?? preset.key}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                    {otherLanguages(preset.label)}
                                </span>
                            </DropdownMenuItem>
                        ))}
                    </DropdownMenuContent>
                </DropdownMenu>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <span>
                            <Button
                                variant="ghost"
                                size="icon-sm"
                                disabled={isEmpty(value)}
                                onClick={() => onChange({})}
                                aria-label="Clear label in every language"
                            >
                                <X />
                            </Button>
                        </span>
                    </TooltipTrigger>
                    <TooltipContent>
                        Clear label in every language
                    </TooltipContent>
                </Tooltip>
                <LanguageSelect
                    options={options}
                    value={language}
                    onChange={setLanguage}
                />
            </div>
            <p className="text-xs text-muted-foreground">
                Picking a suggestion fills every language. Typing edits the
                selected language only.
            </p>
        </div>
    );
}
